import {
  FormFieldDefinition,
  IExternalFieldDependency
} from '@publishing-engine/domain/types/article';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

type IConfig =
  | (FormFieldDefinition & {
      params: { externalFieldDependency?: IExternalFieldDependency };
    })
  | null;

export const isDependencySatisfied = (
  currentValue: any,
  expectedValue?: string | boolean | string[]
): boolean => {
  if (expectedValue === undefined) return true;

  // Case 1: boolean
  if (typeof expectedValue === 'boolean') {
    return currentValue === expectedValue;
  }

  // Case 2: string
  if (typeof expectedValue === 'string') {
    return currentValue === expectedValue;
  }

  // Case 3: string[]
  if (Array.isArray(expectedValue)) {
    return expectedValue.includes(currentValue);
  }

  return false;
};

function asDependentFieldComponent<P extends object & { config: IConfig }>(
  WrappedComponent: React.ComponentType<P>
) {
  function Component(props: P) {
    if (!props.config) return null;
    const { watch, setValue } = useFormContext();
    const {
      params: { externalFieldDependency },
      caseDataFields: { main }
    } = props.config;

    const dependsOnValue = watch(externalFieldDependency?.path ?? 'unknown');

    useEffect(() => {
      if (
        externalFieldDependency?.path &&
        !isDependencySatisfied(dependsOnValue, externalFieldDependency?.value)
      ) {
        setValue(main, undefined);
      }
    }, [
      dependsOnValue,
      externalFieldDependency?.path,
      externalFieldDependency?.value
    ]);

    if (
      externalFieldDependency?.value &&
      !isDependencySatisfied(dependsOnValue, externalFieldDependency?.value)
    ) {
      return null;
    }

    return <WrappedComponent {...props} />;
  }

  const wrappedComponentName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  // @NOTE: This is to make the component name more descriptive in React DevTools
  Component.displayName = `asDependentFieldComponent(${wrappedComponentName})`;

  return Component;
}

export default asDependentFieldComponent;
